import { useState } from "react";
import { costEstimate, getDistance } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Calculator, MapPin, Loader2, Fuel } from "lucide-react";
import { toast } from "sonner";
import { EIA_REGION_OPTIONS } from "@/constants/options";

const money = (v) =>
  v === null || v === undefined ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const BreakRow = ({ label, value, sub, strong, testid }) => (
  <div className={`flex items-center justify-between py-1.5 ${strong ? "border-t border-border/60 mt-1 pt-2" : ""}`}>
    <span className={`text-sm ${strong ? "font-bold" : "text-neutral-600"}`}>
      {label} {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
    </span>
    <span data-testid={testid} className={`font-mono-data tabular-nums ${strong ? "text-lg font-bold text-[#0055FF]" : "text-sm"}`}>{value}</span>
  </div>
);

export const CostEstimator = ({ form, setForm }) => {
  const [autoing, setAutoing] = useState(false);
  const [calcing, setCalcing] = useState(false);
  const set = (patch) => setForm((p) => ({ ...p, ...patch }));
  const markup = form.markup_percent ?? 10;
  const b = form.cost_breakdown;

  const autoDistance = async () => {
    if (!form.pickup_location || !form.delivery_location) {
      toast.error("Enter pickup and delivery locations on the Request tab first");
      return;
    }
    setAutoing(true);
    try {
      const r = await getDistance(form.pickup_location, form.delivery_location);
      set({
        estimated_distance_miles: r.distance_miles, distance_source: r.source,
        pickup_lat: r.pickup.lat, pickup_lng: r.pickup.lng,
        delivery_lat: r.delivery.lat, delivery_lng: r.delivery.lng,
      });
      toast.success(`Distance: ${r.distance_miles} mi (${r.source})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not calculate distance — enter miles manually");
    } finally { setAutoing(false); }
  };

  const calculate = async () => {
    setCalcing(true);
    try {
      const r = await costEstimate({
        truck_id: form.truck_id, trailer_id: form.trailer_id, driver_id: form.driver_id,
        distance_miles: Number(form.estimated_distance_miles || 0),
        estimated_time_hrs: Number(form.estimated_time_hrs || 0),
        markup_percent: Number(markup), eia_region: form.eia_region,
      });
      set({ cost_breakdown: r, estimated_cost: r.total, eia_region: r.eia_region });
      toast.success(`Estimated cost: ${money(r.total)}`);
    } catch (e) { toast.error("Cost calculation failed"); }
    finally { setCalcing(false); }
  };

  return (
    <div className="space-y-4" data-testid="cost-estimator">
      <div className="rounded-md border border-border/60 bg-neutral-50/60 px-3 py-2 text-xs text-neutral-600">
        Cost = distance × (truck + trailer $/mi) + driver $/hr × hours + fuel (EIA diesel ÷ MPG), plus overhead markup.
        Assign truck, trailer & driver on the Assignment tab for accurate rates.
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Distance (miles)</Label>
          <div className="mt-1.5 flex gap-2">
            <Input type="number" data-testid="cost-distance-input" className="h-9 font-mono-data"
              value={form.estimated_distance_miles ?? ""}
              onChange={(e) => set({ estimated_distance_miles: e.target.value === "" ? "" : Number(e.target.value), distance_source: "manual" })} />
            <Button type="button" variant="outline" className="h-9 gap-1.5 whitespace-nowrap" data-testid="auto-distance-btn"
              onClick={autoDistance} disabled={autoing}>
              {autoing ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />} Auto
            </Button>
          </div>
          {form.distance_source && (
            <p className="text-[11px] text-muted-foreground mt-1">Source: {form.distance_source}</p>
          )}
        </div>

        <div>
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Diesel Region (EIA)</Label>
          <div className="mt-1.5">
            <Select value={form.eia_region || ""} onValueChange={(v) => set({ eia_region: v })}>
              <SelectTrigger className="h-9" data-testid="cost-region-select"><SelectValue placeholder="Auto from truck / state" /></SelectTrigger>
              <SelectContent>
                {EIA_REGION_OPTIONS.map((r) => <SelectItem key={r} value={r} data-testid={`cost-region-opt-${r}`}>{r.replaceAll("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Overhead Markup</Label>
          <span data-testid="markup-value" className="font-mono-data text-sm font-bold text-[#0055FF]">{markup}%</span>
        </div>
        <Slider className="mt-3" data-testid="markup-slider" min={0} max={50} step={1}
          value={[markup]} onValueChange={(v) => set({ markup_percent: v[0] })} />
        <div className="flex justify-between text-[10px] text-muted-foreground mt-1"><span>0%</span><span>Default 10%</span><span>50%</span></div>
      </div>

      <Button type="button" data-testid="calculate-cost-btn" onClick={calculate} disabled={calcing}
        className="w-full gap-2 bg-[#0A0A0A] hover:bg-neutral-800">
        {calcing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />}
        Calculate Estimated Cost
      </Button>

      {b && (
        <div className="rounded-md border border-border/60 bg-white p-4" data-testid="cost-breakdown">
          <BreakRow label="Truck" sub={`${b.distance_miles} mi × $${b.truck_rate_per_mile}/mi`} value={money(b.truck_cost)} />
          <BreakRow label="Trailer" sub={`${b.distance_miles} mi × $${b.trailer_rate_per_mile}/mi`} value={money(b.trailer_cost)} />
          <BreakRow label="Driver" sub={`${b.estimated_time_hrs} hr × $${b.driver_rate_per_hour}/hr`} value={money(b.driver_cost)} />
          <BreakRow label="Fuel" sub={`${b.distance_miles} mi ÷ ${b.mpg} mpg × $${b.diesel_price} (${b.eia_region || "n/a"})`} value={money(b.fuel_cost)} />
          <BreakRow label="Subtotal" value={money(b.subtotal)} />
          <BreakRow label={`Overhead markup (${b.markup_percent}%)`} value={money(b.markup_amount)} />
          <BreakRow label="Estimated Total" value={money(b.total)} strong testid="cost-total" />
        </div>
      )}
    </div>
  );
};
